import * as React from 'react';
import { cn } from '../../../utils';
import { zineZeroPad } from './zine-theme';

export interface SpreadProps {
  /** anchor id for agenda scroll (e.g. "s1") */
  id?: string;
  children?: React.ReactNode;
  className?: string;
}

/**
 * A numbered "spread" section of the issue. Adjacent spreads get the 3px
 * black top border separator; each spread has a scroll-margin for anchors.
 */
export function Spread({ id, children, className }: SpreadProps) {
  return (
    <article className={cn('z-spread', className)} id={id}>
      <div className="z-page">{children}</div>
    </article>
  );
}

export interface SpreadHeadProps {
  /** spread number; numbers are zero-padded to the outlined "01" style */
  no: number | string;
  /** eyebrow line, e.g. "Read · 3 min" */
  eyebrow?: React.ReactNode;
  /** uppercase condensed headline */
  title: React.ReactNode;
  className?: string;
}

/** Spread header: outlined big number + eyebrow + condensed h2. */
export function SpreadHead({ no, eyebrow, title, className }: SpreadHeadProps) {
  return (
    <div className={cn('z-spread-head', className)}>
      <span className="z-bigno">{zineZeroPad(no)}</span>
      <div>
        {eyebrow != null && <p className="z-spread-eyebrow">{eyebrow}</p>}
        <h2>{title}</h2>
      </div>
    </div>
  );
}

export interface ColsProps {
  children?: React.ReactNode;
  className?: string;
}

/**
 * Two-column spread layout: prose column (1.5fr) + aside column (1fr),
 * collapsing to a single column at 720px. Place exactly two children
 * (typically a <Prose> and an <aside> of VocabClips).
 */
export function Cols({ children, className }: ColsProps) {
  return <div className={cn('z-cols', className)}>{children}</div>;
}

export interface ProseProps {
  children?: React.ReactNode;
  className?: string;
}

/**
 * Prose wrapper: styles paragraphs (62ch measure), the `.z-lead` first
 * paragraph (serif italic — use <Lead>) and <mark> (pink underline-highlight).
 */
export function Prose({ children, className }: ProseProps) {
  return <div className={cn('z-prose', className)}>{children}</div>;
}

export interface LeadProps {
  children?: React.ReactNode;
  className?: string;
}

/** Serif-italic lead paragraph for the first paragraph inside <Prose>. */
export function Lead({ children, className }: LeadProps) {
  return <p className={cn('z-lead', className)}>{children}</p>;
}
